import React, { useRef, useEffect } from 'react';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiBell, mdiRecord } from '@lumapps/lumx/icons';
import { Emphasis, IconButton, Size, Placement, Offset, Icon, ColorPalette, PopoverDialog } from '@lumapps/lumx/react';
import { InfiniteScroll } from '@lumapps/lumx/react/utils';
import { useSelector } from '@lumapps/redux/react';
import { useOnNavigation } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useDropdown } from '@lumapps/utils/hooks/useDropdown';

import { isNotificationsBetaEnabled } from '../../ducks/selectors';
import { useNotificationLabel } from '../../hooks/useNotificationLabel';
import { NotificationStatus } from '../../types';
import { NotificationList, NotificationListProps } from '../NotificationList';
import { NotificationListHeader, NotificationListHeaderProps, TITLE_ID } from '../NotificationListHeader';

import './index.scss';

export type NotificationMenuProps = NotificationListProps &
    Omit<
        NotificationListHeaderProps,
        'onSettingsClick' | 'onFilterAll' | 'onFilterUnread' | 'notificationStatus' | 'hasMore'
    > & {
        /** Is notifications loading? */
        isLoading?: boolean;
        /** Is the dropdown opened */
        isOpen?: boolean;
        /** Is there at least one notification - read or unread */
        hasNotifications?: boolean;
        /** An error occured while fetching notifications */
        hasError?: boolean;
        /** Function to get notifications count */
        countUnread(): void;
        /** Function to get notifications */
        listNotifications(notificationStatus?: NotificationStatus, cursor?: string): void;
        /** notification count */
        unreadNotificationsCount?: number;
        /** The dropdown placement */
        placement?: Placement;
        /** Is there more notification to load ? */
        more: boolean;
        /** Pagination cursor */
        cursor: string | undefined;
        /** The dropdown offset */
        offset?: Offset;
        /** whether this component should be rendered or not */
        isVisible: boolean;
        /** functions to set notifications as read / unread / deleted in backend */
        setAsRead: (notificationId: string) => void;
        setAsUnread: (notificationId: string) => void;
        deleteNotification(notificationId: string, isNotificationRead: boolean): void;
        /** custom styles passed down to the notifications icon */
        styles?: Record<string, any>;
        /** action triggered when the notifications refreshes */
        refreshNotifications(notificationStatus: NotificationStatus, forceRefresh?: boolean): void;
    };

const CLASSNAME = 'notification-center-menu';
const SCOPE = 'notifications';
const NotificationMenu: React.FC<NotificationMenuProps> = ({
    isOpen = false,
    isLoading = false,
    hasError = false,
    hasNotifications = false,
    countUnread,
    listNotifications,
    unreadNotificationsCount = 0,
    placement = Placement.BOTTOM_END,
    deleteAll,
    setAllAsRead,
    notifications,
    setAsRead,
    setAsUnread,
    deleteNotification,
    more,
    cursor,
    offset = {},
    isVisible = true,
    styles = {},
    refreshNotifications,
}) => {
    const { translateKey } = useTranslate();
    const { element } = useClassnames(CLASSNAME);
    const anchorNotificationDropdownRef = useRef(null);
    const isNotificationsEnabled = useSelector(isNotificationsBetaEnabled);
    const defaultNotificationStatus = isNotificationsEnabled ? NotificationStatus.unread : NotificationStatus.all;
    const [notificationStatus, setNotificationStatus] = React.useState(defaultNotificationStatus);
    const { isOpen: isDropdownOpen, setIsOpen: setDropdownIsOpen } = useDropdown(isOpen);
    const { get } = useDataAttributes(SCOPE);
    const labelRef = useRef(null);
    const forceRefreshRef = useRef(false);
    const buttonTitle = translateKey(GLOBAL.NOTIFICATIONS);
    const notificationsTitle = useNotificationLabel(unreadNotificationsCount);

    const notificationsList = React.useMemo(() => {
        // we only want to filter the list if under feature flag otherwise we just use notifications to not alter
        // the current notification center
        if (isNotificationsEnabled && notificationStatus === NotificationStatus.unread) {
            return notifications.filter(({ notification }) => notification.isRead === false);
        }
        return notifications;
    }, [isNotificationsEnabled, notificationStatus, notifications]);

    const onInfiniteScroll = () => {
        if (more && !isLoading) {
            listNotifications(notificationStatus, cursor);
        }
    };

    useEffect(() => {
        // Count unread notification on button visible.
        if (isVisible) {
            countUnread();
        }
    }, [countUnread, isVisible]);

    useEffect(() => {
        // OLD UI (FF OFF): List or refresh notifications when dropdown opens or filter changes
        if (!isNotificationsEnabled && isDropdownOpen) {
            refreshNotifications(notificationStatus, forceRefreshRef.current);
            forceRefreshRef.current = false;
        }
    }, [isDropdownOpen, notificationStatus, refreshNotifications, isNotificationsEnabled]);

    useOnNavigation(() => {
        // Count unread notification and refresh list in background on react app navigation.
        if (isVisible) {
            countUnread();
            // OLD UI (FF OFF): Refresh list in background if there are new notifications (smart checking)
            if (!isNotificationsEnabled) {
                refreshNotifications(NotificationStatus.all, false);
            }
        }
    }, [countUnread, refreshNotifications, isVisible, isNotificationsEnabled]);

    if (!isVisible) {
        return null;
    }

    const openDropdown = (open: boolean) => () => {
        if (isNotificationsEnabled) {
            if (open === true) {
                // NEW UI (FF ON): Always fetch ALL notifications when opening dropdown to know complete state
                // OLD UI (FF OFF): Fetching is handled by useEffect based on notificationStatus
                listNotifications(NotificationStatus.all);
            } else {
                setNotificationStatus(NotificationStatus.unread);
            }
        }

        setDropdownIsOpen(open);
    };

    const setNotificationRead = (notificationId: string, isRead: boolean, shouldCloseDropdown = true) => {
        if (shouldCloseDropdown) {
            setDropdownIsOpen(false);
        }

        if (isRead) {
            setAsRead(notificationId);
        } else {
            setAsUnread(notificationId);
        }
    };

    const onSettingsClick = () => {
        openDropdown(false)();
    };

    const handleFilterAll = () => {
        if (notificationStatus !== NotificationStatus.all) {
            setNotificationStatus(NotificationStatus.all);
            // OLD UI (FF OFF): Force refresh when switching filters
            if (!isNotificationsEnabled) {
                forceRefreshRef.current = true;
            }
        }
    };

    const handleFilterUnread = () => {
        if (notificationStatus !== NotificationStatus.unread) {
            setNotificationStatus(NotificationStatus.unread);
            // OLD UI (FF OFF): Force refresh when switching filters
            if (!isNotificationsEnabled) {
                forceRefreshRef.current = true;
            }
        }
    };

    return (
        <div className={CLASSNAME}>
            {unreadNotificationsCount > 0 && (
                <Icon className={element('badge')} color={ColorPalette.red} icon={mdiRecord} size={Size.xs} />
            )}

            <IconButton
                ref={anchorNotificationDropdownRef}
                emphasis={Emphasis.low}
                icon={mdiBell}
                size={Size.m}
                onClick={openDropdown(!isDropdownOpen)}
                className={element('button', { 'is-active': isDropdownOpen })}
                color={styles.themeColor}
                label={`${buttonTitle}, ${notificationsTitle}`}
                {...get({ element: 'menu', type: unreadNotificationsCount.toString() })}
            />
            <PopoverDialog
                aria-labelledby={TITLE_ID}
                isOpen={isDropdownOpen}
                anchorRef={anchorNotificationDropdownRef}
                placement={placement}
                onClose={openDropdown(isOpen)}
                offset={offset}
                fitWithinViewportHeight
                focusElement={labelRef}
                className={element('popover')}
            >
                <div className={element('popover-content')}>
                    <NotificationListHeader
                        hasError={hasError}
                        hasNotifications={hasNotifications}
                        unreadNotificationsCount={unreadNotificationsCount}
                        isLoading={isLoading}
                        deleteAll={deleteAll}
                        setAllAsRead={setAllAsRead}
                        onSettingsClick={onSettingsClick}
                        labelRef={labelRef}
                        notificationStatus={notificationStatus}
                        onFilterAll={handleFilterAll}
                        onFilterUnread={handleFilterUnread}
                    />
                    <NotificationList
                        notifications={notificationsList}
                        setNotificationRead={setNotificationRead}
                        deleteNotification={deleteNotification}
                        closeNotificationMenu={() => setDropdownIsOpen(false)}
                        hasError={hasError}
                        hasNotifications={hasNotifications}
                        isLoading={isLoading}
                        listNotifications={listNotifications}
                    />
                    {more && !isLoading && <InfiniteScroll callback={onInfiniteScroll} />}
                </div>
            </PopoverDialog>
        </div>
    );
};

export { NotificationMenu, CLASSNAME };
